import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

// Zoom-responsive heatmap. At low zoom (national view) we want smaller,
// tightly-blurred kernels so dense states (CA/TX/FL) don't dominate as a
// single uniform blob. At high zoom (street level) we widen kernels so
// individual points read clearly. Without this, the same heatLayer applied
// across z=4..14 looks washed-out at one extreme and pixelated at the other.
const radiusForZoom = (zoom) => {
  if (zoom <= 4) return 12;
  if (zoom <= 6) return 18;
  if (zoom <= 8) return 24;
  if (zoom <= 10) return 30;
  if (zoom <= 12) return 36;
  return 42;
};
const blurForZoom = (zoom) => {
  if (zoom <= 4) return 8;
  if (zoom <= 6) return 12;
  if (zoom <= 8) return 16;
  if (zoom <= 10) return 20;
  return 24;
};

const HeatmapLayer = ({ points, options = {} }) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!points || points.length === 0) return;

    // Normalize intensities so a few extreme outliers don't blow out the
    // gradient. Use the 95th percentile as the effective max instead of the
    // raw max — this keeps the heat readable on long-tailed distributions.
    const intensities = points.map(p => p.intensity || 1);
    const sorted = [...intensities].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] || 1;
    const denom = Math.max(p95, 1);

    const heatPoints = points.map(p => [
      p.latitude,
      p.longitude,
      Math.min(1, (p.intensity || 1) / denom),
    ]);

    const buildOptions = (z) => ({
      radius: radiusForZoom(z),
      blur: blurForZoom(z),
      maxZoom: 14,
      max: 1.0,
      minOpacity: 0.35,
      gradient: {
        0.2: '#22c55e',
        0.4: '#3b82f6',
        0.6: '#f59e0b',
        0.8: '#ef4444',
        1.0: '#7c2d12',
      },
      ...options,
    });

    const heatLayer = L.heatLayer(heatPoints, buildOptions(map.getZoom()));
    heatLayer.addTo(map);
    layerRef.current = heatLayer;

    const onZoom = () => {
      try {
        heatLayer.setOptions(buildOptions(map.getZoom()));
      } catch (e) {
        /* noop — older leaflet.heat versions lack setOptions */
      }
    };
    map.on('zoomend', onZoom);

    return () => {
      map.off('zoomend', onZoom);
      map.removeLayer(heatLayer);
      layerRef.current = null;
    };
  }, [map, points, options]);

  return null;
};

export default HeatmapLayer;
